import { z } from "zod";

const NAME_REGEX = /^[a-zA-Z\s'.-]+$/;
const LOCATION_REGEX = /^[a-zA-Z\s,.-]+$/;
const HAS_LETTER = /[a-zA-Z]/;
const HAS_LETTER_PAIR = /[a-zA-Z]{2,}/;

export const personNameSchema = z
  .string()
  .trim()
  .min(3, "Name must be at least 3 characters")
  .max(100, "Name must be at most 100 characters")
  .regex(NAME_REGEX, "Name contains invalid characters")
  .refine(
    (val) => {
      const parts = val.split(/\s+/).filter(Boolean);
      return parts.length >= 2 || (parts.length === 1 && parts[0].length >= 4);
    },
    {
      message:
        "Enter your first and last name, or a single name of at least 4 characters",
    },
  );

export const shortNameSchema = z
  .string()
  .trim()
  .min(3, "Name must be at least 3 characters")
  .max(100, "Name must be at most 100 characters")
  .regex(NAME_REGEX, "Name contains invalid characters");

export const companyNameSchema = z
  .string()
  .trim()
  .min(3, "Company name must be at least 3 characters")
  .max(100, "Company name must be at most 100 characters")
  .refine((val) => HAS_LETTER.test(val), "Company name must include a letter");

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\d+$/, "Phone must contain digits only")
  .min(7, "Phone must be at least 7 digits")
  .max(15, "Phone must be at most 15 digits");

export const locationSchema = z
  .string()
  .trim()
  .min(3, "Location must be at least 3 characters")
  .max(100, "Location must be at most 100 characters")
  .regex(LOCATION_REGEX, "Location contains invalid characters")
  .refine((val) => HAS_LETTER_PAIR.test(val), "Enter a valid location");

export const requirementTextSchema = z
  .string()
  .trim()
  .min(10, "Requirement must be at least 10 characters")
  .max(1000, "Requirement must be at most 1000 characters")
  .refine((val) => HAS_LETTER.test(val), "Requirement must include a letter");

export const optionalMessageSchema = z
  .string()
  .trim()
  .max(1000, "Message must be at most 1000 characters")
  .superRefine((val, ctx) => {
    if (!val) return;
    if (val.length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Message must be at least 5 characters when provided",
      });
    } else if (!HAS_LETTER.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Message must include a letter",
      });
    }
  })
  .transform((val) => (val === "" ? undefined : val));

export const contactMessageSchema = z
  .string()
  .trim()
  .min(10, "Message must be at least 10 characters")
  .max(2000, "Message must be at most 2000 characters")
  .refine((val) => HAS_LETTER.test(val), "Message must include a letter");

export const quoteMessageSchema = z
  .string()
  .trim()
  .min(10, "Message must be at least 10 characters")
  .max(1000, "Message must be at most 1000 characters")
  .refine((val) => HAS_LETTER.test(val), "Message must include a letter");

/** Strip non-digits and cap at 15 characters (use on phone field onChange). */
export function sanitizePhoneInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 15);
}

/** Map Zod issues to one message per top-level field. */
export function zodFieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? "");
    if (key && !out[key]) {
      out[key] = issue.message;
    }
  }
  return out;
}
